import { Group, Popover, Select, SelectItem, Stack } from '@mantine/core';
import { GridApi, IFilterParams, IFloatingFilterParams } from 'ag-grid-community';
import { DateInput } from 'components/inputs/DateInput';
import { FilterChangeCallback } from 'components/tables/filters/hooks/use-filter-value';
import { get, isEqual, noop } from 'lodash';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

type DateFilterType = 'equals' | 'greaterThan' | 'lessThan' | 'inRange' | 'blank' | 'notBlank';

interface DateFilterModel {
  filterType: 'date';
  type: DateFilterType;
  dateFrom?: string;
  dateTo?: string;
}

interface DateFilterState {
  type: DateFilterType;
  dateFrom: string;
  dateTo: string;
}

interface DateFilterProps {
  api: GridApi;
  filterKey: string;
  onChange?: FilterChangeCallback;
  parentFilterInstance?: IFloatingFilterParams['parentFilterInstance'];
}

export interface ConfiguredDateFilterProps {
  api: GridApi;
  onChange?: FilterChangeCallback;
}

interface ConfigureDateFilterProps {
  filterKey: string;
}

const DEFAULT_STATE: DateFilterState = {
  type: 'equals',
  dateFrom: '',
  dateTo: '',
};

const OPTIONS: SelectItem[] = [
  { value: 'equals', label: 'Rovná sa' },
  { value: 'greaterThan', label: 'Po' },
  { value: 'lessThan', label: 'Pred' },
  { value: 'inRange', label: 'Medzi' },
  { value: 'blank', label: 'Prázdne' },
  { value: 'notBlank', label: 'Nie je prázdne' },
];

/**
 * Finds display label for a filter type.
 */
const getFilterTypeLabel = (type: DateFilterType): string =>
  OPTIONS.find((option) => option.value === type)?.label ?? type;

/**
 * Normalizes supported cell values into a `YYYY-MM-DD` string.
 */
const getDateString = (value: unknown): string => {
  if (value instanceof Date) {
    const day = value.getDate().toString().padStart(2, '0');
    const month = (value.getMonth() + 1).toString().padStart(2, '0');
    const year = value.getFullYear().toString();

    return `${year}-${month}-${day}`;
  }

  if (typeof value !== 'string') {
    return '';
  }

  const match = value.match(/^\d{4}-\d{2}-\d{2}/);
  return match?.[0] ?? '';
};

/**
 * Converts AG Grid date model to local component state.
 */
const getStateFromModel = (model?: DateFilterModel | null): DateFilterState => ({
  type: model?.type ?? DEFAULT_STATE.type,
  dateFrom: model?.dateFrom ?? '',
  dateTo: model?.dateTo ?? '',
});

/**
 * Converts local component state to AG Grid date model.
 */
const createModel = ({ type, dateFrom, dateTo }: DateFilterState): DateFilterModel | null => {
  if (type === 'blank' || type === 'notBlank') {
    return { filterType: 'date', type };
  }

  if (!dateFrom) {
    return null;
  }

  if (type === 'inRange') {
    if (!dateTo) {
      return null;
    }

    return { filterType: 'date', type, dateFrom, dateTo };
  }

  return { filterType: 'date', type, dateFrom };
};

/**
 * Builds a compact one-line summary shown in the floating input.
 */
const getSummary = (state: DateFilterState): string => {
  const model = createModel(state);

  if (!model) {
    return '';
  }

  const label = getFilterTypeLabel(model.type);

  if (model.type === 'blank' || model.type === 'notBlank') {
    return label;
  }

  const from = model.dateFrom?.split('-').reverse().join('.') ?? '';
  const to = model.dateTo?.split('-').reverse().join('.') ?? '';

  if (model.type === 'inRange') {
    return `${from} – ${to}`;
  }

  if (model.type === 'equals') {
    return from;
  }

  return `${label}: ${from}`;
};

/**
 * AG Grid parent filter implementation for date strings without timezone (`YYYY-MM-DD`).
 */
export const DateColumnFilter = forwardRef(({ filterChangedCallback, colDef }: IFilterParams, ref) => {
  const model = useRef<DateFilterModel | null>(null);

  useImperativeHandle(ref, () => ({
    doesFilterPass({ data }: { data: Record<string, any> }) {
      const filterModel = model.current;

      if (!filterModel) {
        return true;
      }

      const field = colDef.field;
      const value = field ? get(data, field) : undefined;
      const cellDate = getDateString(value);

      if (filterModel.type === 'blank') {
        return !cellDate;
      }

      if (filterModel.type === 'notBlank') {
        return !!cellDate;
      }

      if (!cellDate || !filterModel.dateFrom) {
        return false;
      }

      if (filterModel.type === 'equals') {
        return cellDate === filterModel.dateFrom;
      }

      if (filterModel.type === 'greaterThan') {
        return cellDate > filterModel.dateFrom;
      }

      if (filterModel.type === 'lessThan') {
        return cellDate < filterModel.dateFrom;
      }

      if (!filterModel.dateTo) {
        return false;
      }

      return cellDate >= filterModel.dateFrom && cellDate <= filterModel.dateTo;
    },

    getModel() {
      return model.current;
    },

    setModel(newModel: DateFilterModel | null) {
      model.current = newModel;
    },

    isFilterActive() {
      return !!model.current;
    },

    onFloatingFilterChanged(newModel: DateFilterModel | null) {
      if (!isEqual(model.current, newModel)) {
        model.current = newModel;
        filterChangedCallback();
      }
    },
  }));

  return <></>;
});

interface DateFilterRef {
  onParentModelChanged: () => void;
}

/**
 * Floating date filter UI used in AG Grid header and in table profile modal.
 */
const DateFilter = forwardRef<DateFilterRef, DateFilterProps>(function DateFilter(
  { api, parentFilterInstance = noop, filterKey, onChange },
  ref
) {
  const [state, setState] = useState<DateFilterState>(() => getStateFromModel(api.getFilterModel()[filterKey]));
  const [opened, setOpened] = useState(false);

  useImperativeHandle(ref, () => ({ onParentModelChanged: noop }));

  const pushModel = useCallback(
    (nextState: DateFilterState) => {
      const nextModel = createModel(nextState);

      if (onChange) {
        onChange({ [filterKey]: nextModel });
      } else {
        parentFilterInstance((instance: unknown) => {
          (instance as { onFloatingFilterChanged: (model: DateFilterModel | null) => void }).onFloatingFilterChanged(
            nextModel
          );
        });
      }
    },
    [filterKey, onChange, parentFilterInstance]
  );

  const setAndPush = useCallback(
    (nextState: DateFilterState) => {
      setState(nextState);
      pushModel(nextState);
    },
    [pushModel]
  );

  const onTypeChange = useCallback(
    (newType: DateFilterType) => {
      if (newType === 'blank' || newType === 'notBlank') {
        setAndPush({ type: newType, dateFrom: '', dateTo: '' });
        return;
      }

      if (newType === 'inRange') {
        setAndPush({ type: newType, dateFrom: state.dateFrom, dateTo: state.dateTo });
        return;
      }

      setAndPush({ type: newType, dateFrom: state.dateFrom, dateTo: '' });
    },
    [setAndPush, state.dateFrom, state.dateTo]
  );

  useEffect(() => {
    /**
     * Handles grid model updates and keeps local floating filter state in sync.
     */
    const handleModelUpdated = ({ api }: { api: GridApi }) => {
      const model = api.getFilterModel()[filterKey] as DateFilterModel | null | undefined;

      setState((previous) => {
        if (!model) {
          return opened ? previous : DEFAULT_STATE;
        }

        return getStateFromModel(model);
      });
    };

    api.addEventListener('modelUpdated', handleModelUpdated);
    return () => api.removeEventListener('modelUpdated', handleModelUpdated);
  }, [api, filterKey, opened]);

  const showDateFrom = useMemo(() => state.type !== 'blank' && state.type !== 'notBlank', [state.type]);
  const showDateTo = useMemo(() => state.type === 'inRange', [state.type]);
  const summary = useMemo(() => getSummary(state), [state]);

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-start" width={300} withinPortal>
      <Popover.Target>
        <Select
          data={[summary]}
          size="sm"
          w="100%"
          value={summary}
          placeholder="Kedykoľvek"
          onFocus={() => setOpened(true)}
          clearable={!!summary}
          onChange={(value) => {
            if (!value) {
              setAndPush(DEFAULT_STATE);
              setOpened(false);
            }
          }}
          styles={{ dropdown: { display: 'none' } }}
        />
      </Popover.Target>

      <Popover.Dropdown>
        <Stack spacing="sm">
          <Select
            size="sm"
            data={OPTIONS}
            value={state.type}
            onChange={(value) => value && onTypeChange(value as DateFilterType)}
            allowDeselect={false}
          />

          <Group spacing={8} grow noWrap>
            {showDateFrom && (
              <DateInput
                size="sm"
                clearable
                placeholder={showDateTo ? 'Od' : 'Dátum'}
                popoverProps={{ withinPortal: false }}
                value={state.dateFrom}
                onChange={(dateFrom) => setAndPush({ ...state, dateFrom })}
              />
            )}

            {showDateTo && (
              <DateInput
                size="sm"
                clearable
                placeholder="Do"
                popoverProps={{ withinPortal: false }}
                value={state.dateTo}
                onChange={(dateTo) => setAndPush({ ...state, dateTo })}
              />
            )}
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
});

type DateFilterComponent = React.ForwardRefExoticComponent<
  React.PropsWithoutRef<DateFilterProps> & React.RefAttributes<DateFilterRef>
> & {
  configure: (config: ConfigureDateFilterProps) => (props: ConfiguredDateFilterProps) => React.JSX.Element;
};

const DateFilterWithConfigure = DateFilter as DateFilterComponent;

DateFilterWithConfigure.configure = (config: ConfigureDateFilterProps) => (props: ConfiguredDateFilterProps) =>
  <DateFilterWithConfigure {...config} {...props} />;

export default DateFilterWithConfigure;
